import React, { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Fab,
  Skeleton,
  Snackbar,
  Stack,
  Typography
} from '@mui/material'
import {
  accordionSummaryStyles,
  cardStyles,
  skeletonRectangularStyles,
  stackSpacing
} from './organization-urls-styles'
import { type BaseUrl, type UrlDescType, deleteUrl, patchOrgUrlDisplayOrder, patchPersonUrlDisplayOrder, isOrgUrl, isPersonUrl, type PersonUrl, type OrgUrl } from 'services'
import AddIcon from '@mui/icons-material/Add'

import { UrlSourceCard } from './url-source-card'
import { CenteredCircularProgress, DragAndDropStack } from 'components'
import { type HttpResponse } from 'interfaces'

export interface UrlsProps {
  originalUrls: BaseUrl[]
  createNewUrl: () => BaseUrl
  urlDescTypes: UrlDescType[]
  loading: boolean
  title: string
  triggerReload: () => void
  disableDrag?: boolean
}

export const UrlsBlock: React.FC<UrlsProps> = ({ originalUrls, createNewUrl, triggerReload, urlDescTypes, loading, title, disableDrag = false }) => {
  const [expanded, setExpanded] = useState(true)
  const [showLoading, setShowLoading] = useState(false)
  const [urlToAdd, setUrlToAdd] = useState<BaseUrl | undefined>()
  const initialDisabledState = new Map<number, boolean>()
  originalUrls.forEach((url) => {
    initialDisabledState.set(url.id, true)
  })
  const [disabledMap, setDisabledMap] = useState<Map<number, boolean>>(initialDisabledState)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = useState(false)
  const initialErrorState = new Map<number, boolean>()
  originalUrls.forEach((url) => {
    initialErrorState.set(url.id, false)
  })

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const addUrl = (): void => {
    if (urlToAdd === undefined || originalUrls.length === 0) {
      const newUrl = createNewUrl()
      setUrlToAdd(newUrl)
      setDisabledMap(new Map(disabledMap.set(newUrl.id, false)))
    }
  }

  const handleDeleteConfirmed = (url: BaseUrl): void => {
    if (url == null) { return }

    setShowLoading(true)
    void deleteUrl(url).then(value => {
      setShowLoading(true)
      if (value) {
        triggerReload()
      } else {
        console.log('error')
        setShowUnsuccessAlert(true)
      }
    }).catch(() => {
      console.log('error')
      setShowUnsuccessAlert(true)
    }).finally(() => {
      setShowLoading(false)
    })
  }

  const handleDelete = (url: BaseUrl): void => {
    if (url == null) {
      return
    }

    if (url === urlToAdd || url.id <= 0) {
      setUrlToAdd(undefined)
      return
    }

    handleDeleteConfirmed(url)
  }
  const renderCard = (url: BaseUrl, index: number): React.ReactNode => {
    return <UrlSourceCard
    key={index}
    index={index}
    title={title}
    triggerReload={() => {
      setUrlToAdd(undefined)
      triggerReload()
    }}
    originalUrlBase={url}
    urlDescTypes={urlDescTypes}
    originalDisabled={disabledMap.get(url.id) ?? true}
    deleteUrl={handleDelete}
    />
  }

  const changeDisplayOrder = async (items: BaseUrl[], urlSelected: BaseUrl): Promise<HttpResponse<BaseUrl[]>> => {
    const isUrlFromOrg: boolean = isOrgUrl(items[0] as OrgUrl)
    const isUrlFromPerson: boolean = isPersonUrl(items[0] as PersonUrl)
    const displayOrderById = items.reduce<Record<number, number>>((acc, url, index) => ({ ...acc, [url.id]: index + 1 }), {})

    try {
      setShowLoading(true)

      if (isUrlFromOrg) {
        const url = items[0] as OrgUrl
        return await patchOrgUrlDisplayOrder(url.orgId, title.toLocaleLowerCase(), displayOrderById, urlSelected.id)
      } else if (isUrlFromPerson) {
        const url = items[0] as PersonUrl
        return await patchPersonUrlDisplayOrder(url.personId, title.toLocaleLowerCase(), displayOrderById, urlSelected.id)
      }
      throw new Error('Failed to update display : Must Have OrgId or PersonID')
    } catch (error: any) {
      console.error(error)
      setShowUnsuccessAlert(true)
      throw new Error('Failed to update display : Must Have OrgId or PersonID')
    } finally {
      setShowLoading(false)
    }
  }

  return (
    <>
    <CenteredCircularProgress loading={showLoading} size={70}></CenteredCircularProgress>
    <Card sx={cardStyles}>
        <Snackbar open={showSuccessAlert} autoHideDuration={3000} onClose={() => { setShowSuccessAlert(false) } }>
        <Alert severity="success">{`${title} deleted successfully.`}</Alert>
      </Snackbar>
      <Snackbar open={showUnsuccessAlert} autoHideDuration={3000} onClose={() => { setShowUnsuccessAlert(false) } }>
        <Alert severity="error">{`Failed to Delete ${title}.`}</Alert>
      </Snackbar>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={accordionSummaryStyles}>
          <Typography variant="h6">{title + 's'}</Typography>

        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Stack sx={stackSpacing}>
              {loading
                ? (
                  <>
                    <Skeleton variant="rectangular" sx={skeletonRectangularStyles} key={1}/>
                    <Skeleton variant="rectangular" sx={skeletonRectangularStyles} key={2}/>
                    <Skeleton variant="rectangular" sx={skeletonRectangularStyles} key={3}/>
                  </>
                  )
                : (
                  <>
                  <Stack>
                  <DragAndDropStack
                    id={'urls-stack'}
                    originalItems={originalUrls}
                    itemRenderer={(item: BaseUrl, index: number) => renderCard(item, index)}
                    updateDisplayOrder={async (items: BaseUrl[], urlSelected: BaseUrl) => await changeDisplayOrder(items, urlSelected)}
                    disableDrag={disableDrag || originalUrls.length <= 1}
                    makeGray={false}
                  />
                  {urlToAdd != null && renderCard(urlToAdd, originalUrls.length)}
                  </Stack>
                  </>
                  )}
              <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                <Fab color="primary" aria-label="add" onClick={() => { addUrl() } }>
                  <AddIcon />
                </Fab>
              </Box>
            </Stack>
          </CardContent>
        </AccordionDetails>
      </Accordion>

    </Card></>
  )
}
